import { TestStatusIcon } from '@snapi/icons';
import { TestStatus } from '@snapi/types';
import { Button, Subtitle } from '@snapi/luxury';
import Link from 'next/link';
import { format } from 'date-fns';

export interface MicrobiomeTestCardProps {
  id: string;
  status: TestStatus;
  createdAt: string;
}

const PendingCard = () => (
  <div className="flex flex-row pl-3 pr-5 h-20 md:h-[92px] md:pl-4 md:pr-10 bg-red rounded-[10px]">
    <div className="flex flex-col flex-1 text-white justify-center">
      <Subtitle>
        Today is a test day!
        <br />
        Activate your new sample
      </Subtitle>
    </div>
    <div className="flex items-center justify-center">
      <TestStatusIcon height={60} status={TestStatus.received} />
    </div>
  </div>
);

const ReceivedCard = ({ id, createdAt }: { id: string; createdAt: string }) => (
  <div className="flex flex-row pl-3 pr-5 md:pl-4 bg-purple rounded-[10px] h-20">
    <div className="flex flex-col flex-1 justify-center">
      <div className="flex flex-col gap-2">
        <Subtitle>
          <span className="font-light">Status:</span> Awaiting sample
        </Subtitle>
        <Subtitle>
          <span className="font-light">Test Kit ID:</span> {id}
          <br />
          <span className="font-light">Date Taken:</span>{' '}
          {format(new Date(createdAt), 'MMM do, yyyy')}
        </Subtitle>
      </div>
    </div>
    <div className="flex items-center justify-center">
      <TestStatusIcon height={60} status={TestStatus.analyzing} />
    </div>
  </div>
);

const AnalyzingCard = ({
  id,
  createdAt,
}: {
  id: string;
  createdAt: string;
}) => (
  <div className="flex flex-row pl-3 pr-5 h-20 md:h-[92px] md:pl-4 md:pr-10 bg-purple rounded-[10px]">
    <div className="flex flex-col flex-1 justify-center">
      <div className="flex flex-col gap-2">
        <Subtitle>
          <span className="font-light">Status:</span> Lab sequencing
        </Subtitle>
        <Subtitle>
          <span className="font-light">Test Kit ID:</span> {id}
          <br />
          <span className="font-light">Date Taken:</span>{' '}
          {format(new Date(createdAt), 'MMM do, yyyy')}
        </Subtitle>
      </div>
    </div>
    <div className="flex items-center justify-center">
      <TestStatusIcon height={60} status={TestStatus.pending} />
    </div>
  </div>
);

export const MicrobiomeTestCard: React.FC<MicrobiomeTestCardProps> = ({
  id,
  status,
  createdAt,
}) => {
  if (status === TestStatus.pending) {
    return <PendingCard />;
  }

  if (status === TestStatus.received) {
    return <ReceivedCard id={id} createdAt={createdAt} />;
  }

  if (status === TestStatus.analyzing) {
    return <AnalyzingCard id={id} createdAt={createdAt} />;
  }

  return (
    <div className="flex flex-1 flex-col gap-1">
      <div className="flex flex-row pl-3 pr-5 md:pl-4 bg-purple rounded-[10px] h-20">
        <div className="flex flex-col flex-1 justify-center">
          <div className="flex flex-col gap-2">
            <Subtitle>
              <span className="font-light">Status:</span> Result ready
            </Subtitle>
            <Subtitle>
              <span className="font-light">Test Kit ID:</span> {id}
              <br />
              <span className="font-light">Date Taken:</span>{' '}
              {format(new Date(createdAt), 'MMM do, yyyy')}
            </Subtitle>
          </div>
        </div>
        <div className="flex items-center justify-center">
          <TestStatusIcon height={60} status={TestStatus.ready} />
        </div>
      </div>
      <Link href={`/dashboard/report/${id}`}>
        <Button size="lg">See the report</Button>
      </Link>
    </div>
  );
};
